import {computed, defineComponent, onMounted, reactive, ref} from '@vue/composition-api';
import { SettingsContext } from "@/utilities/settings-context";
import { alertDialogue, generalError } from "@/functions/system-modal-functions";
import { i18n } from '@/utilities/vue-i18n';
import { RawLocation } from 'vue-router';
import TransitionSlide from "@/components/transition-slide/transition-slide.vue";
import { createPageContext } from "@/components/navbar-box/page-context";
import { OtpType } from '@/components/otp/otp-types';
import OtpModal from '@/components/otp/otp-modal.vue';
import { ModalIconType } from '@/components/system-modals/system-modals';
import {AxiosError} from "axios";
import {
    getExternalAuthenticationContext,
    onExternalAuthentication, onExternalEmailConfirmation, onExternalRegistration,
    setAuthenticationToken
} from "@/functions/authentication-functions";
import {Accounts, validateEmail} from "@noordigitalagency/accounts-ledigajobb";


export default defineComponent({

    props: {
        message: String
    },

    components: {
        TransitionSlide,
        OtpModal
    },

    setup(props, context) {

        const accounts = new Accounts(SettingsContext.constants.accountsApiDomainName);

        const googleButton = ref<HTMLElement>();

        const passwordLength = SettingsContext.get('signUp.passwordLength') as number;

        const settings = reactive({

            showOtp: false,

            otpEmail: '',

            otpType: OtpType.SignInOrRegister
        })

        const model = reactive({ email: '', password: '' });

        const states = reactive({
            error: ''
        });

        function otpShow(email: string) {

            getExternalAuthenticationContext().cancelGoogleOneTap();

            settings.otpEmail = email;

            settings.showOtp = true;
        }

        onMounted(async () => {

            getExternalAuthenticationContext().insertGoogleButton(googleButton.value as HTMLElement, {width: `${googleButton.value?.clientWidth}px`, type: 'standard', text: "signup_with"});

            getExternalAuthenticationContext().showGoogleOneTap(SettingsContext.constants.stage !== 'production');

            const email = context.root.$route.query.email as string;

            if (email) {

                try {

                    await accounts.activateAccount(email);

                    otpShow(email);
                }
                catch{

                    await generalError();
                }
            }
        });

        onExternalRegistration(registrationPayload => {

            getExternalAuthenticationContext().cancelGoogleOneTap();

            context.root.$router.replace({

                name: 'User Registration',

                params: {registration: registrationPayload as unknown as string}
            });
        });

        async function redirectAfterAuthentication() {

            const redirectUrl = Array.isArray(context.root.$route.query.redirect) ? context.root.$route.query.redirect[0] : context.root.$route.query.redirect;

            const uri = new URL(`${location.origin}${decodeURI((redirectUrl ?? ''))}`);

            const route: RawLocation = {

                path: uri.pathname,
                hash: uri.hash,
                query: uri.search?.split('?')?.[1]?.split('&').reduce((previousValue, currentValue) => {

                    const parts = currentValue.split('=');

                    if (parts.length === 2) {

                        previousValue[parts[0]] = parts[1];
                    }

                    return previousValue;

                }, {} as Record<string, string>),
            };

            await context.root.$router.replace(route);
        }

        async function repopulateFileSystemsAndRedirectToNewPage(token: string) {
            getExternalAuthenticationContext().cancelGoogleOneTap();

            accounts.updateToken(token);

            setAuthenticationToken(await accounts.makeRecruiter());
            
            await redirectAfterAuthentication();
        }

        onExternalAuthentication(token => {

            accounts.updateToken(token);

            accounts.makeRecruiter().then(newToken => {

                repopulateFileSystemsAndRedirectToNewPage(newToken).then();
            });
        })

        onExternalEmailConfirmation(email => otpShow(email));

        function otpClosed() {

            settings.showOtp = false;
        }

        const otpConfirmed = async (payload: {otpCode: string}) => {

            try {

                otpClosed();

                await repopulateFileSystemsAndRedirectToNewPage(await accounts.confirmEmail(model.email, payload.otpCode));

            } catch (e) {

                await generalError();
            }
        }

        const submit = async () => {

            try {

                await repopulateFileSystemsAndRedirectToNewPage(await accounts.logIn(model.email, model.password));
            }
            catch (e) {

                if ((e as AxiosError).response?.status == 412) {

                    /// If status == 412 the User need to activate.
                    otpShow(model.email);
                }
                else if ((e as AxiosError).response?.status == 400) {

                    await alertDialogue(i18n.t('dialogue.error-general-title') as string, i18n.t('pages.sign-in.error') as string, ModalIconType.error)
                }
                else {

                    await generalError();
                }
            }
        };

        const messageContent = computed(() => {
            switch (props.message) {
                case 'confirm':
                    return i18n.t('pages.sign-in.confirm-email'); // The user has signed up and now have to go to their email and confirm the account
                case 'confirmed':
                    return i18n.t('pages.sign-in.email-confirmed'); // The user has clicked on the confirmation link in their email and have confirmed their account, and now they have to sign in
                case 'reset':
                    return i18n.t('pages.sign-in.password-reset'); // The user is coming form the password reset page and the password it reset and now the user should sign in with the new password
                case 'forgot':
                    return i18n.t('pages.sign-in.forgot-password'); // The user is coming from the forgotten password page and now the user must go to their account and click on the link which was sent to them for resetting their password
                default:
                    return null;
            }
        });

        const pageContext = createPageContext({
            actions: [],
            title: { text: i18n.t('general.sign-in') as string },
            breadcrumb: {
                showHome: false,
                segments: []
            }
        });

        return {

            model,

            settings,

            passwordLength,

            submit,

            messageContent,

            states,

            validateEmail,

            pageContext,

            OtpType,

            otpClosed,

            otpConfirmed,

            googleButton
        }
    }
});
